const SESSION_KEY = 'operacao.accessToken'
const LOCAL_ACCESS_TOKEN_KEY = 'accessToken'
const QUERY_KEYS = ['accessToken', 'access_token', 'token']
const DASHBOARD_LOGIN_URL =
  process.env.VUE_APP_DASHBOARD_URL || 'https://portal.propositive.com.br'
const PORTAL_ORIGIN =
  (process.env.VUE_APP_PORTAL_ORIGIN || 'https://portal.propositive.com.br').trim()
/** Contrato Pulse (mesmo do financeiro / ModuleFrameView). */
const AUTH_TOKEN_EVENT = 'AUTH_TOKEN'
const MODULO_PRONTO_EVENT = 'MODULO_PRONTO'
const TOKEN_EXPIRED_EVENT = 'TOKEN_EXPIRED'
const SESSAO_ENCERRADA_EVENT = 'SESSAO_ENCERRADA'
/** Legado GPDF — ainda aceito para nao quebrar hosts antigos. */
const PORTAL_AUTH_EVENT_TYPE = 'PORTAL_AUTH'
const COLABORADOR_READY_EVENT_TYPE = 'COLABORADOR_READY'
const IFRAME_AUTH_WAIT_MS = 8000
const TOKEN_REFRESH_TIMEOUT_MS = 8000

function getAllowedPortalOrigins () {
  const fromList = String(process.env.VUE_APP_PORTAL_ORIGINS || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  const origins = fromList.length ? fromList : [PORTAL_ORIGIN]
  if (PORTAL_ORIGIN && !origins.includes(PORTAL_ORIGIN)) {
    origins.unshift(PORTAL_ORIGIN)
  }
  return origins
}

function isAllowedPortalOrigin (origin) {
  if (!origin) return false
  return getAllowedPortalOrigins().includes(origin)
}

/** Origin do pai para postMessage (referrer se estiver na allowlist). */
function resolveParentTargetOrigin () {
  try {
    if (document.referrer) {
      const refOrigin = new URL(document.referrer).origin
      if (isAllowedPortalOrigin(refOrigin)) return refOrigin
    }
  } catch (e) {
    /* ignore */
  }
  return getAllowedPortalOrigins()[0] || PORTAL_ORIGIN
}

export function getAccessToken () {
  const fromSession = sessionStorage.getItem(SESSION_KEY)
  if (fromSession && fromSession.trim()) return fromSession.trim()

  const fromMainApp = localStorage.getItem(LOCAL_ACCESS_TOKEN_KEY)
  if (fromMainApp && fromMainApp.trim()) return fromMainApp.trim()

  if (process.env.NODE_ENV === 'development') {
    const dev = process.env.VUE_APP_DEV_JWT
    if (dev && dev.trim()) return dev.trim()
  }

  try {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null')
    if (user && user.accessToken) return String(user.accessToken).trim()
  } catch (e) {
    /* ignore */
  }

  return null
}

export function setAccessToken (token) {
  if (!token) {
    sessionStorage.removeItem(SESSION_KEY)
    return
  }
  sessionStorage.setItem(SESSION_KEY, token.trim())
}

function decodeJwtPayload (token) {
  try {
    const base64 = token.split('.')[1]
    if (!base64) return null
    const normalized = base64.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

function normalizeRoles (roles) {
  if (Array.isArray(roles)) {
    return roles.map((r) => {
      if (typeof r === 'string') return r.trim()
      if (r && typeof r === 'object') return (r.authority || r.name || r.role || '').trim()
      return String(r || '').trim()
    }).filter(Boolean)
  }
  if (typeof roles === 'string' && roles.trim()) {
    return roles.split(',').map((r) => r.trim()).filter(Boolean)
  }
  return []
}

function buildUserFromPayload (payload, token) {
  const claims = decodeJwtPayload(token) || {}
  const roles = normalizeRoles(payload.roles || claims.roles || claims.authorities)
  const username =
    payload.username ||
    payload.subject ||
    payload.login ||
    claims.sub ||
    claims.username ||
    ''

  return {
    accessToken: token,
    nome:
      payload.nome ||
      payload.name ||
      claims.nome ||
      claims.name ||
      username ||
      '',
    username,
    login: payload.login || username,
    usuario: payload.usuario || username,
    id: payload.id ?? payload.userId ?? claims.id ?? claims.userId ?? null,
    roles,
    acesso_previous: payload.acesso_previous || claims.acesso_previous || '',
    empresa: payload.empresa || claims.empresa || '',
    setor: payload.setor || payload.departamento || claims.setor || '',
    departamento: payload.departamento || claims.departamento || '',
    checktoken: true,
  }
}

function readStoredUser () {
  try {
    return JSON.parse(sessionStorage.getItem('user') || 'null')
  } catch (e) {
    return null
  }
}

function persistUserSession (user) {
  sessionStorage.setItem('user', JSON.stringify(user))
  if (user.username) {
    localStorage.setItem('username', user.username)
  }
  if (user.nome) {
    localStorage.setItem('nome', user.nome)
  }
  if (Array.isArray(user.roles)) {
    localStorage.setItem('roles', JSON.stringify(user.roles))
  }
  return user
}

/** Monta/atualiza sessionStorage.user a partir da resposta de GET /auth/validar-token */
export function applyValidarTokenResponse (token, apiResponse) {
  const existing = readStoredUser()
  const fromApi = buildUserFromPayload(apiResponse || {}, token)

  const user = {
    ...(existing && existing.accessToken === token ? existing : {}),
    ...fromApi,
    accessToken: token,
    nome: apiResponse?.nome || fromApi.nome,
    id: apiResponse?.id ?? fromApi.id,
    username: apiResponse?.subject || apiResponse?.username || fromApi.username,
    login: apiResponse?.subject || apiResponse?.login || fromApi.login,
    usuario: apiResponse?.subject || apiResponse?.usuario || fromApi.usuario,
    roles: normalizeRoles(apiResponse?.roles || fromApi.roles),
    checktoken: true,
  }

  return persistUserSession(user)
}

export function syncUserSession () {
  const token = getAccessToken()
  if (!token) return null

  const existing = readStoredUser()

  if (
    existing &&
    existing.accessToken === token &&
    existing.nome &&
    existing.id != null &&
    Array.isArray(existing.roles) &&
    existing.roles.length > 0
  ) {
    return existing
  }

  try {
    const payload = JSON.parse(localStorage.getItem('auth_payload') || 'null')
    if (payload) {
      const user = buildUserFromPayload(payload, token)
      return persistUserSession(user)
    }
  } catch (e) {
    /* ignore */
  }

  const claims = decodeJwtPayload(token) || {}
  const user = buildUserFromPayload(claims, token)
  return persistUserSession(user)
}

export function initAuthTokenFromUrl () {
  const params = new URLSearchParams(window.location.search)
  let found = null

  for (const key of QUERY_KEYS) {
    const value = params.get(key)
    if (value && value.trim()) {
      found = value.trim()
      params.delete(key)
      break
    }
  }

  if (!found) return

  setAccessToken(found)
  localStorage.setItem(LOCAL_ACCESS_TOKEN_KEY, found)

  const qs = params.toString()
  const query = qs ? `?${qs}` : ''
  const path = `${window.location.pathname}${query}${window.location.hash}`
  window.history.replaceState(window.history.state, '', path)
}

function normalizarToken (payload) {
  if (!payload) return null
  const accessToken = String(payload.accessToken || '').trim()
  if (accessToken) return accessToken
  const token = String(payload.token || '').trim()
  if (token) return token
  return null
}

function salvarCampoString (chave, valor) {
  const normalizado = String(valor || '').trim()
  if (!normalizado) return
  localStorage.setItem(chave, normalizado)
}

export function applyPortalAuth (payload) {
  if (!payload) return

  localStorage.setItem('auth_payload', JSON.stringify(payload))

  const token = normalizarToken(payload)
  if (token) {
    setAccessToken(token)
    localStorage.setItem(LOCAL_ACCESS_TOKEN_KEY, token)
    localStorage.setItem('token', token)
  }

  salvarCampoString('username', payload.username)
  salvarCampoString('nome', payload.nome)
  if (payload.roles != null) {
    localStorage.setItem('roles', JSON.stringify(payload.roles))
  }
  salvarCampoString('tokenType', payload.tokenType)
  salvarCampoString('type', payload.type)

  if (token) {
    const user = buildUserFromPayload(payload, token)
    persistUserSession(user)
  }
}

function aplicarTokenDoPortal (token) {
  if (!token || typeof token !== 'string') return
  const trimmed = token.trim()
  if (!trimmed) return
  setAccessToken(trimmed)
  localStorage.setItem(LOCAL_ACCESS_TOKEN_KEY, trimmed)
  const claims = decodeJwtPayload(trimmed) || {}
  applyPortalAuth({
    accessToken: trimmed,
    token: trimmed,
    tokenType: 'Bearer',
    username: claims.email || claims.sub || '',
    nome: claims.nome || claims.name || '',
    roles: claims.roles || [],
    email: claims.email || '',
  })
  console.log('[auth] token OK (postMessage)')
}

export function initPortalAuthBridge () {
  const parentOrigin = resolveParentTargetOrigin()
  console.log('[auth] portal bridge', {
    parentOrigin,
    allowed: getAllowedPortalOrigins(),
    referrer: document.referrer || null,
  })

  const onPortalMessage = (event) => {
    if (!isAllowedPortalOrigin(event.origin)) {
      console.warn('[auth] postMessage ignorado (origin nao permitida)', event.origin)
      return
    }
    const data = event.data
    if (!data || typeof data !== 'object') return

    // Pulse padrao
    if (data.type === AUTH_TOKEN_EVENT && typeof data.token === 'string') {
      aplicarTokenDoPortal(data.token)
      return
    }
    if (data.type === SESSAO_ENCERRADA_EVENT) {
      logoutToPortal()
      return
    }

    // Legado GPDF (PORTAL_AUTH)
    if (data.type === PORTAL_AUTH_EVENT_TYPE) {
      applyPortalAuth(data.payload)
      console.log('[auth] token OK (PORTAL_AUTH)')
    }
  }

  window.addEventListener('message', onPortalMessage)

  if (window.parent !== window) {
    // Pulse
    window.parent.postMessage({ type: MODULO_PRONTO_EVENT }, parentOrigin)
    // Legado (harmless se o pai Pulse ignorar)
    window.parent.postMessage({ type: COLABORADOR_READY_EVENT_TYPE }, parentOrigin)
  }
}

/** Pede novo access token ao portal Pulse (ex.: apos 401). */
export function solicitarNovoToken () {
  return new Promise((resolve) => {
    if (window.parent === window) {
      resolve(null)
      return
    }

    const parentOrigin = resolveParentTargetOrigin()
    const timeout = window.setTimeout(() => {
      window.removeEventListener('message', handler)
      resolve(null)
    }, TOKEN_REFRESH_TIMEOUT_MS)

    function handler (event) {
      if (!isAllowedPortalOrigin(event.origin)) return
      if (event.data?.type === AUTH_TOKEN_EVENT && typeof event.data.token === 'string') {
        window.clearTimeout(timeout)
        window.removeEventListener('message', handler)
        aplicarTokenDoPortal(event.data.token)
        resolve(event.data.token)
      }
      if (event.data?.type === SESSAO_ENCERRADA_EVENT) {
        window.clearTimeout(timeout)
        window.removeEventListener('message', handler)
        resolve(null)
      }
    }

    window.addEventListener('message', handler)
    window.parent.postMessage({ type: TOKEN_EXPIRED_EVENT }, parentOrigin)
  })
}

function resolveApiBaseUrl () {
  const envUrl = (process.env.VUE_APP_API_URL || '').trim()
  return envUrl && envUrl.length > 0 ? envUrl.replace(/\/$/, '') : '/api'
}

function isTokenValido (response) {
  if (response.valido === true) return true
  if (typeof response.codigo === 'string' && response.codigo.trim().toUpperCase() === 'TOKEN_VALIDO') {
    return true
  }
  if (typeof response.status === 'string' && response.status.trim().toUpperCase() === 'TOKEN_VALIDO') {
    return true
  }
  return false
}

function waitForPortalAuth () {
  if (getAccessToken()) return Promise.resolve(true)
  if (window.parent === window) return Promise.resolve(false)

  return new Promise((resolve) => {
    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      if (getAccessToken()) {
        window.clearInterval(timer)
        resolve(true)
        return
      }
      if (Date.now() - startedAt >= IFRAME_AUTH_WAIT_MS) {
        window.clearInterval(timer)
        resolve(false)
      }
    }, 100)
  })
}

export async function validarTokenOuRedirecionar () {
  const apiBase = resolveApiBaseUrl()
  console.log('[auth] validarTokenOuRedirecionar', { apiBase, dashboard: DASHBOARD_LOGIN_URL })

  if (!getAccessToken()) {
    console.log('[auth] sem token — aguardando portal auth', IFRAME_AUTH_WAIT_MS, 'ms')
    await waitForPortalAuth()
  }

  const token = getAccessToken()
  if (!token) {
    console.warn('[auth] sem token após espera — redirect', DASHBOARD_LOGIN_URL)
    window.location.href = DASHBOARD_LOGIN_URL
    return false
  }

  console.log('[auth] token presente (len)', token.length)

  try {
    const url = `${apiBase}/auth/validar-token`
    console.log('[auth] GET', url)
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })

    console.log('[auth] validar-token status', response.status)
    if (!response.ok) {
      console.warn('[auth] token rejeitado pela API — redirect', DASHBOARD_LOGIN_URL)
      window.location.href = DASHBOARD_LOGIN_URL
      return false
    }

    const payload = await response.json()
    if (!isTokenValido(payload)) {
      console.warn('[auth] payload inválido — redirect', payload)
      window.location.href = DASHBOARD_LOGIN_URL
      return false
    }

    applyValidarTokenResponse(token, payload)
    console.log('[auth] token OK')
    return true
  } catch (e) {
    console.error('[auth] falha ao validar token (API offline?)', e)
    window.location.href = DASHBOARD_LOGIN_URL
    return false
  }
}

export function logoutToPortal () {
  setAccessToken(null)
  sessionStorage.removeItem('user')
  sessionStorage.removeItem('selectedModule')
  localStorage.removeItem('auth_payload')
  window.location.href = DASHBOARD_LOGIN_URL
}
